'use client'

import {
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices
} from '@/types/typo3'
import { Container, Section, Skeleton } from '@/ui/components'
import { RatingStarsProps } from '@/ui/components/RatingStars/RatingStars'
import {
  SpotSanityDetailHead,
  SpotSanityDetailHero,
  SpotSanityDetailInfo
} from '@/ui/components/SpotSanity/SpotSanity'
import { SectionFeature, SectionGallery } from '@/ui/Sections'
import dynamic from 'next/dynamic'
import { memo, Suspense } from 'react'

const SidebarRestBarShop = dynamic(
  () => import('./Sidebar/SidebarRestBarShop'),
  { ssr: false }
)

const SidebarEvent = dynamic(() => import('./Sidebar/SidebarEvent'), {
  ssr: false
})

const SidebarAccomodation = dynamic(
  () => import('./Sidebar/SidebarAccomodation'),
  {
    ssr: false
  }
)

const Sidebar = dynamic(() => import('./Sidebar/Sidebar'), { ssr: false })

const Tomas = dynamic(() => import('@/ui/components/Tomas/Tomas'), {
  ssr: false
})

interface Props {
  data:
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityEventList
  variant?: 'restBarShop' | 'event' | 'default' | 'accomodation'
}

const SpotSanityDetail = ({ data, variant = 'default' }: Props) => {
  const showText = false

  return (
    <>
      <SpotSanityDetailHero
        data={data}
        showText={showText}
        stars={
          (data.content?.item?.stars &&
            (data.content?.item?.stars.replace(
              'stars-',
              ''
            ) as unknown as RatingStarsProps['stars'])) ||
          undefined
        }
      />
      <SectionGallery images={data.content?.item?.images} />
      <Section
        className={'s-spotsanity-detail pt-xl'}
        id={data.id?.toString()}
        anim="fade-in"
      >
        <Container className={'gap-gutter grid lg:grid-cols-3'}>
          <aside>
            <div className="top-gutter sticky">
              <Suspense fallback={<Skeleton className="h-20 w-full" />}>
                {variant === 'restBarShop' ? (
                  <SidebarRestBarShop
                    data={data as Typo3SectionSpotSanityEatDrink}
                  />
                ) : variant === 'event' ? (
                  <SidebarEvent
                    data={data as Typo3SectionSpotSanityEventList}
                  />
                ) : variant === 'accomodation' ? (
                  <SidebarAccomodation
                    data={data as Typo3SectionSpotSanityAccomodation}
                  />
                ) : (
                  <Sidebar
                    data={
                      data as
                        | Typo3SectionSpotSanityService
                        | Typo3SectionSpotSanityNature
                        | Typo3SectionSpotSanitySportServices
                        | Typo3SectionSpotSanityCulture
                        | Typo3SectionSpotSanitySport
                        | Typo3SectionSpotSanityShopping
                        | Typo3SectionSpotSanityFreetime
                    }
                  />
                )}
              </Suspense>
            </div>
          </aside>
          <div
            className={
              'gap-y-2xl flex flex-col lg:col-span-2 lg:col-start-1 lg:row-start-1'
            }
          >
            <SpotSanityDetailHead data={data} />

            {data.content?.item?.videos &&
              data.content?.item?.videos?.length > 0 &&
              data.content?.item?.videos?.[0]?.properties?.extension ===
                'youtube' && (
                <iframe
                  src={data.content?.item?.videos[0]?.publicUrl as string}
                  width="100%"
                  className="aspect-video w-full"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}

            <div>
              {data?.content?.item &&
                'objectIdTomas' in data?.content?.item &&
                data?.content?.item?.hasWidgetTomas === true && (
                  <Tomas id={data?.content?.item?.objectIdTomas as string} />
                )}
              <SpotSanityDetailInfo data={data} />
            </div>
          </div>
        </Container>
      </Section>

      {data.content?.item?.listRelatedEvents?.length ? (
        <SectionFeature
          items={data.content?.item?.listRelatedEvents}
          variant="topEvent"
        />
      ) : null}
    </>
  )
}

export default memo(SpotSanityDetail)
